import React, { useState } from "react";
import { Box, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { AssetFinanceUrl } from "../../../../config";
import Form from "./form";
import DynamicTableForJson from "../../../../data/DynamicTable/dynamicTableForJson";
import Joyride from "react-joyride"; // Import Joyride
import Swal from "sweetalert2";
import { executeAnyMutation } from "../../../../data/DynamicServiceNew/jsondynamicservice";
import { Chip } from "@mui/material";

const columns = [
  {
    field: "DocumentName",
    headerName: "Document Name",
    flex: 2,
  },
  {
    field: "Requirement",
    headerName: "Requirement",
    width: 150,
    valueGetter: (params) => {
      switch (params.row.Requirement) {
        case "REQUIRED":
          return "Mandatory";
        case "CONDITIONAL":
          return "If Applicable";
        case "SUPPORTING":
          return "Optional";
        default:
          return params.row.Requirement;
      }
    },
  },
  {
    field: "RequiredAboveAmount",
    headerName: "Required Above Amount",
    width: 180,
    valueGetter: (params) =>
      params.row.RequiredAboveAmount != null
        ? `KES ${Number(params.row.RequiredAboveAmount).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`
        : "-",
  },
  {
    field: "RequiredForLien",
    headerName: "Required For Lien",
    width: 150,
    valueGetter: (params) =>
      params.row.RequiredForLien ? "Yes" : "No",
  },
  {
    field: "RequiredIfThirdParty",
    headerName: "Required If Third Party",
    width: 180,
    valueGetter: (params) =>
      params.row.RequiredIfThirdParty ? "Yes" : "No",
  },
  {
    field: "DisplayOrder",
    headerName: "Display Order",
    width: 130,
  },
  {
    field: "IsActive",
    headerName: "Status",
    width: 120,
    renderCell: (params) => (
      <Chip
        label={params.row.IsActive ? "Active" : "Inactive"}
        size="small"
        color={params.row.IsActive ? "success" : "error"}
        sx={{ fontWeight: 600 }}
      />
    ),
  },
];
const base_url = AssetFinanceUrl.uri;

// Define tour steps for the Rights page
const rightsTourSteps = [
  {
    target: ".dynamic-table-header",
    content: "This is the Asset Type Document Requirement setup page, where you can manage document requirements for different asset types.",
    placement: "top",
    disableBeacon: true,
  },
  {
    target: ".dynamic-table-search",
    content: "Use the search bar to find specific Asset Types by name or other criteria.",
    placement: "bottom",
  },
  {
    target: ".dynamic-table-add-button",
    content: "Click this button to add a new Asset Type.",
    placement: "right",
  },
  // {
  //   target: ".dynamic-table-grid",
  //   content: "This table displays all rights. You can view and manage them here.",
  //   placement: "top",
  // },
  {
    target: ".dynamic-table-actions",
    content: "Select actions to be able to Edit each Asset Type from this dropdown.",
    placement: "left",
  },
];

const AssetTypeDocumentRequirementTable = (props) => {
  const navigate = useNavigate();
  const [runTour, setRunTour] = useState(false); // ▶️ State to control Joyride
  const rawId = props?.Id || localStorage.getItem("AssetTypeId");
  const [selectedRow, setSelectedRow] = useState(null);

   const handleToggleActive = async (row, refetch) => {
      const isCurrentlyActive = !!row?.IsActive;
      const action = isCurrentlyActive ? "Deactivate" : "Activate";
      const confirm = await Swal.fire({
        title: `${action} Asset Type?`,
        text: `Are you sure you want to ${action.toLowerCase()} "${row.DocumentName}"?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes",
      });
      if (!confirm.isConfirmed) return;
  
      const response = await executeAnyMutation(
        "updateAssetDocumentRequirement",
        {
          id: row.Id,
          assetType: parseInt(assetTypeId),
          requirement: row.Requirement,
          requiredForLien: row.RequiredForLien,
          requiredAboveAmount: row.RequiredAboveAmount
            ? parseFloat(row.RequiredAboveAmount)
            : null,
          requiredIfThirdParty: row.RequiredIfThirdParty,
          documentName: row.DocumentName,
          displayOrder: parseInt(row.DisplayOrder),
          policyVersion: parseInt(row.PolicyVersion) || null,
          effectiveFrom: row.effectiveFrom || null,
          effectiveTo: row.effectiveTo || null,
          companyId: row.CompanyId,
          isActive: !isCurrentlyActive,
        },
        "assetDocumentRequirement",
        false,
        base_url
      );
  
      if (response?.results) {
        await Swal.fire("Done!", `Asset Document Requirement has been ${!isCurrentlyActive ? "activated" : "deactivated"}.`, "success");
        if (refetch) refetch();
      } else {
        await Swal.fire("Error!", "Could not update the Asset Document Requirement. Please try again.", "error");
      }
    };

  if (props?.Id) {
    localStorage.setItem("AssetTypeId", props.Id);
  }
  const assetTypeId = rawId && !isNaN(parseInt(rawId)) ? parseInt(rawId) : null;
  const actions = {
    entity: localStorage.getItem("SelectedTabLabel"),
    edit: {
      key: "EDIT",
      button_name: "Edit",
      Show_Button: true,
      permission: "EditPermission",
    },
    add: {
      key: "ADD",
      button_name: "Add",
      Show_Button: true,
      permission: "CreatePermission",
    },
    toggleActive: {
      key: "TOGGLE_ACTIVE",
      button_name: "Activate/Deactivate",
      Show_Button: true,
      permission: "EditPermission",
      onClick: (row, refetch) => handleToggleActive(row, refetch),
    },
    dataObject: {
      ignoreFields: ["CreatedAt", "CreatedBy", "UpdatedAt", "UpdatedBy", "MetadataSchema", "CompanyId", "AssetType"],
      objectName: "assetDocumentRequirements",
      parameters: { assetType: assetTypeId },
      inputObjectName: "requirement",
    },
    isSubRight: true,
    showStatus: true,
    showMore: {
      search: false,
      min: 0,
      max: 0
    }
  };
    const handleAction = (actionType, row) => {
    if (actionType === "toggleActivate") {
      setSelectedRow(row);
    }
    };

  return (
    <Box m="20px">
      {/* Joyride Component */}
      <Joyride
        steps={rightsTourSteps}
        run={runTour}
        continuous
        showSkipButton
        showProgress
        styles={{
          options: {
            primaryColor: "#1976d2",
            zIndex: 10000,
          },
        }}
      />
      {/* Top Bar */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Button
          onClick={() => navigate("/asset-finance-setups")}
          variant="contained"
          color="secondary"
        >
          Back to Asset Types
        </Button>
        <Button onClick={() => setRunTour(true)} variant="outlined" color="primary">
          Start Page Tour
        </Button>
      </Box>
      <DynamicTableForJson
        title="Asset Type Document Requirements"
        subtitle="Manage Document Requirements for Asset Types"
        FormComponent={Form}
        columns={columns}
        base_url={base_url}
        actions={actions}
        onRowClick={(row) => handleAction(actions.toggleActive, row)}

      />
    </Box>
  );
};

export default AssetTypeDocumentRequirementTable;
